import { Alert, Box, Paper, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { FileDropZone } from "../../../../components/UIToolkit/Inputs/FileDropZone";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { ACCEPTED_TYPES, DROP_ZONE_HINT } from "../../utils";
import { WeekParityConfirmDialog } from "../../WeekParityConfirmDialog";
import { LoadOrderCaption } from "../LoadOrderCaption";
import { DataLoadFileRow } from "./DataLoadFileRow";
import { DataLoadResultCard } from "./DataLoadResultCard";
import { DataLoadSharedControls } from "./DataLoadSharedControls";
import { isPending } from "./dataLoadOrder";
import { useDataLoadSection } from "./useDataLoadSection";

/**
 * One run, many files, many kinds. Replaces the old Transform / Create Nurses / Load tabs, which were
 * the same job cut three ways.
 */
export function DataLoadSection() {
	const s = useDataLoadSection();

	const runLabel = s.isRecording ? "Saving prompts..." : s.isTransforming ? "Transforming..." : "Run";
	// The same predicate runLoads uses, not a looser one: counting files that have no payload yet made
	// the button offer uploads the chain then silently skipped.
	const uploadableCount = s.orderedFiles.filter(isPending).length;
	const unhandledCount = s.files.length - s.orderedFiles.filter((file) => file.skip || file.loadResult !== null || file.transformError !== null).length;

	return (
		<Paper data-testid="data-load-section" sx={{ p: 3, minHeight: "fit-content", overflow: "visible" }}>
			<Typography variant="h5" sx={{ mb: 1 }}>
				Data Load
			</Typography>
			<Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
				Drop in every file for this department at once. Label each one, choose whether it needs the AI transform or goes up as-is, and for a roster whether it
				creates or updates people. Files are loaded one at a time, in the order shown.
			</Typography>

			<FileDropZone
				multiple
				accept={ACCEPTED_TYPES.join(",")}
				hint={`${DROP_ZONE_HINT} (one or more)`}
				onFiles={s.addFiles}
				onRejected={s.reportRejected}
				dataTestId="data-load-drop-zone"
			/>

			{s.files.length > 0 && (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
					{s.files.map((file, index) => (
						<DataLoadFileRow
							key={file.id}
							file={file}
							index={index}
							locked={s.locked}
							onKindChange={(kind) => s.setKind(file.id, kind)}
							onModeChange={(mode) => s.setMode(file.id, mode)}
							onRosterActionChange={(action) => s.setRosterAction(file.id, action)}
							onPromptChange={(prompt) => s.setPrompt(file.id, prompt)}
							onTabInstructionChange={(sheet, value) => s.setTabInstruction(file.id, sheet, value)}
							onRemove={() => s.removeFile(file.id)}
						/>
					))}
				</Box>
			)}

			<DataLoadSharedControls
				departmentId={s.departmentId}
				showAllowDuplicates={s.hasShiftsOrTemplate}
				allowDuplicates={s.allowDuplicates}
				onAllowDuplicatesChange={s.setAllowDuplicates}
				showMinStartDate={s.hasTemplate}
				minStartDate={s.minStartDate}
				onMinStartDateChange={s.setMinStartDate}
				locked={s.locked}
			/>

			{s.unsetKindCount > 0 && (
				<Alert severity="info" sx={{ mb: 2 }} data-testid="data-load-unset-kind-warning">
					{`Pick a type for ${s.unsetKindCount} file(s) before running. A file loaded as the wrong type goes to the wrong loader.`}
				</Alert>
			)}

			{s.hasUndatedDirectShifts && (
				<Alert severity="warning" sx={{ mb: 2 }} data-testid="data-load-undated-warning">
					An as-is shifts file that isn't a CSV can't be dated here, so it loads after every dated shifts file. If that's the wrong chronology, transform it
					instead or upload it on its own.
				</Alert>
			)}

			<LoadOrderCaption entries={s.loadOrderEntries} dataTestId="data-load-order" />

			<Box sx={{ display: "flex", gap: 1, mt: 2 }}>
				<SystemButton
					onClick={() => void s.runTransforms()}
					disabled={!s.canRun}
					loading={s.isRecording || s.isTransforming}
					data-testid="data-load-run-button"
				>
					{runLabel}
				</SystemButton>
				{s.files.length > 0 && (
					<SystemButton buttonVariant="secondary" onClick={s.resetAll} disabled={s.locked} data-testid="data-load-reset-button">
						Reset
					</SystemButton>
				)}
			</Box>

			{s.phase === "results" && (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 3 }} data-testid="data-load-results">
					{s.orderedFiles.map((file, index) => (
						<DataLoadResultCard
							key={file.id}
							file={file}
							index={index}
							departmentId={s.departmentId}
							timezone={s.chosenDepartment?.timezone}
							isNextInOrder={s.nextToUpload?.id === file.id}
							isLoading={s.isLoading}
							locked={s.locked}
							onSkipChange={(skip) => s.setSkip(file.id, skip)}
							onFilteredBlobChange={(blob) => s.updateFile(file.id, { filteredBlob: blob })}
							onEarliestDateChange={(date) => s.updateFile(file.id, { earliestDate: date })}
							onUpload={() => void s.uploadOne(file)}
							onRemove={() => s.removeFile(file.id)}
						/>
					))}

					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
						{s.allSettled ? (
							<Typography variant="body2" sx={{ color: "text.secondary" }} data-testid="data-load-all-settled">
								Every file has been handled.
							</Typography>
						) : (
							unhandledCount > 0 && (
								<Typography variant="body2" sx={{ color: "text.secondary" }} data-testid="data-load-unhandled">
									{`${unhandledCount} file(s) still need attention.`}
								</Typography>
							)
						)}
						<SystemButton
							onClick={() => void s.uploadAll()}
							disabled={s.isLoading || uploadableCount === 0}
							loading={s.isLoading}
							data-testid="data-load-upload-all-button"
						>
							{uploadableCount === 0 ? "All uploaded" : `Upload all (${uploadableCount})`}
						</SystemButton>
					</Box>
				</Box>
			)}

			{s.chosenDepartment?.first_sunday && (
				<WeekParityConfirmDialog
					open={s.showWeekParityDialog}
					department={s.chosenDepartment}
					today={DateTime.now()}
					isSwitching={s.isUpdatingFirstSunday}
					onSwitch={s.handleSwitchAndResume}
					onKeepAsIs={s.handleKeepAsIsAndResume}
					onCancel={() => s.setShowWeekParityDialog(false)}
				/>
			)}
		</Paper>
	);
}
